import { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { 
  Lock, 
  Clock, 
  Sparkles, 
  ArrowRight, 
  Loader2,
  AlertTriangle,
  Crown,
  XCircle
} from 'lucide-react';

// Context for subscription status
const SubscriptionContext = createContext(null);

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) {
        setSubscription(null);
        setLoading(false);
        return;
      }

      try {
        const res = await subscriptionAPI.getStatus();
        console.log('Subscription status:', res.data);
        setSubscription(res.data);
      } catch (error) {
        console.error('Failed to fetch subscription status:', error);
        // On error, default to expired status to be safe
        setSubscription({ can_access: false, is_active: false, status: 'expired' });
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user]);

  const refreshSubscription = async () => {
    if (!user) return;
    try {
      const res = await subscriptionAPI.getStatus();
      setSubscription(res.data);
    } catch (error) {
      console.error('Failed to refresh subscription');
    }
  };

  return (
    <SubscriptionContext.Provider value={{ subscription, loading, refreshSubscription }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Paywall component - Blocks access for non-subscribers
export const Paywall = ({ children }) => {
  const { user } = useAuth();
  const { subscription, loading } = useSubscription();
  const navigate = useNavigate();

  // Not logged in - show children (let other auth handle it)
  if (!user) {
    return children;
  }

  // Still loading subscription status
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  // User has access - show content
  if (subscription?.can_access) {
    return children;
  }

  // User access revoked
  if (subscription?.status === 'revoked') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFFFFF] to-[#FFEDDF] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl border-4 border-red-300 shadow-hard p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-[#1E3A5F] mb-3">
            Access Revoked
          </h2>
          
          <p className="text-[#4A4A4A] mb-6">
            Your access to AMSCompass has been revoked. Please contact support if you believe this is an error.
          </p>
          
          <div className="space-y-3">
            <a href="mailto:support@amscompass.com">
              <Button className="w-full bg-[#1E3A5F] hover:bg-[#2d4a6f] text-white">
                Contact Support
              </Button>
            </a>
            <Link to="/">
              <Button variant="outline" className="w-full">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Trial expired or no subscription
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFFFFF] to-[#FFEDDF] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl border-4 border-[#1E3A5F] shadow-hard p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center mx-auto mb-6">
          <Lock className="w-8 h-8 text-[#1E3A5F]" />
        </div>
        
        <h2 className="text-2xl font-bold text-[#1E3A5F] mb-3">
          {subscription?.status === 'expired' ? 'Trial Expired' : 'Subscription Required'}
        </h2>
        
        <p className="text-[#4A4A4A] mb-6">
          {subscription?.status === 'expired' 
            ? 'Your free trial has ended. Subscribe now to continue using all AMSCompass features.'
            : 'Subscribe to AMSCompass to access all features and grow your artisan business.'}
        </p>
        
        <div className="space-y-3">
          <Link to="/pricing">
            <Button className="w-full bg-[#1E3A5F] hover:bg-[#2d4a6f] text-white font-bold text-lg py-6">
              <Sparkles className="w-5 h-5 mr-2" />
              View Pricing
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <Link to="/">
            <Button variant="outline" className="w-full">
              Back to Home
            </Button>
          </Link>
        </div>
        
        <p className="text-xs text-[#71717A] mt-6">
          Questions? Contact us at support@amscompass.com
        </p>
      </div>
    </div>
  );
};

// Trial banner component - Shows remaining trial days
export const TrialBanner = () => {
  const { subscription, loading } = useSubscription();
  const { user } = useAuth();

  // Don't show if not logged in or still loading
  if (!user || loading) return null;

  // Don't show for active, lifetime, or revoked users
  if (!subscription || subscription.status === 'active' || subscription.status === 'lifetime' || subscription.status === 'revoked') {
    return null;
  }

  // Show trial banner only for users in trial
  if (subscription.status === 'trial' && subscription.trial_days_remaining !== null) {
    const daysLeft = subscription.trial_days_remaining;
    const isUrgent = daysLeft <= 2;
    
    return (
      <div className={`px-4 py-2 text-center text-sm font-medium ${
        isUrgent 
          ? 'bg-red-100 text-red-800 border-b border-red-200' 
          : 'bg-blue-100 text-blue-800 border-b border-blue-200'
      }`}>
        <div className="flex items-center justify-center gap-2">
          {isUrgent ? (
            <AlertTriangle className="w-4 h-4" />
          ) : (
            <Clock className="w-4 h-4" />
          )}
          <span>
            {daysLeft === 0 
              ? 'Your trial ends today!' 
              : daysLeft === 1 
                ? '1 day left in your trial' 
                : `${daysLeft} days left in your free trial`}
          </span>
          <Link to="/pricing" className="underline font-bold hover:no-underline">
            Subscribe now
          </Link>
        </div>
      </div>
    );
  }

  return null;
};

// Status badge for showing subscription status in UI
export const SubscriptionBadge = () => {
  const { subscription, loading } = useSubscription();
  const { user } = useAuth();

  if (!user || loading || !subscription) return null;

  const badges = {
    trial: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock, label: 'Trial' },
    active: { bg: 'bg-green-100', text: 'text-green-700', icon: Sparkles, label: 'Pro' },
    lifetime: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Crown, label: 'Lifetime' },
    expired: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertTriangle, label: 'Expired' },
    revoked: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Revoked' }
  };

  const badge = badges[subscription.status] || badges.trial;
  const Icon = badge.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
      <Icon className="w-3 h-3" />
      {badge.label}
    </span>
  );
};

export default Paywall;
